import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  X,
  Type,
  Hash,
  ToggleLeft,
  Users,
  List,
  LayoutGrid,
  MousePointerClick,
  Sparkles,
  UsersRound,
  Bot,
  Wrench,
} from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import { teamIdQueryParam } from 'shared/utils/teamQueryParam';
import { getSessionUserProperties, getDefaultSessionOperator } from 'features/activity/config/sessionFilterProperties';
import Icon from 'shared/components/Icon';
import { SpanKindIcon } from 'shared/components/spans/KindTag';
import { activityFilterPopupTheme as analyticsTheme } from 'shared/theme/activityFilterPopupTheme';
import {
  getPropertyDescriptionPreview,
  getFirstTimeFilterDescription,
  getCohortFilterDescription,
} from 'features/activity/components/propertyFilterDescriptions';
import { primary as PRIMARY } from "shared/theme/trodoColors";

// Legacy agent-source values still recognised for back-compat with saved
// boards. New filters from this picker emit propertySource='agent' or
// 'span' and don't go through this list.
const AGENT_SOURCES = ['agent_call', 'agent_tool', 'agent_error', 'agent_response', 'agent_feedback'];

/**
 * Module-level catalog cache for entity-property fetches. Survives
 * remount so re-opening the global filter popup is instant after the
 * first fetch.
 */
const _entityCatalogCache = new Map(); // 'agent' | 'span' → row[]
const _entityCatalogInflight = new Map();

/**
 * Convert a /insights/entity-properties response into the flat row shape
 * the picker renderer expects: { name, label, type, source, sampleValues,
 * agentGroup }. The `name` includes the JSONB-path prefix (e.g.
 * `attributes.foo`) so the backend compiler routes it through the right
 * SQL accessor (`attributes->>'foo'`).
 */
function flattenEntityCatalog(catalog, kind) {
  if (!catalog) return [];
  const out = [];
  const labelFor = (s) => kind === 'span'
    ? (s === 'fixed' ? 'Span properties' : 'Span attributes')
    : (s === 'fixed' ? 'Run properties'
      : s === 'metadata' ? 'Run metadata'
      : 'Run attributes');
  const prefixFor = (s) => kind === 'span'
    ? (s === 'fixed' ? '' : 'attributes.')
    : (s === 'fixed' ? '' : (s === 'metadata' ? 'metadata.' : 'attributes.'));

  const pushRows = (arr, defaultSource) => {
    (arr || []).forEach((p) => {
      const inner = p.source || defaultSource;
      const prefix = prefixFor(inner);
      out.push({
        name: prefix + p.name,
        label: p.name,
        type: p.type || 'string',
        source: kind, // 'agent' | 'span'  — drives backend dispatch
        sampleValues: p.sampleValues || [],
        agentGroup: labelFor(inner),
      });
    });
  };

  pushRows(catalog.fixed,  'fixed');
  pushRows(catalog.custom, 'custom');
  return out;
}

async function fetchEntityCatalog(teamId, kind) {
  const cacheKey = `${teamId || ''}:${kind}`;
  if (_entityCatalogCache.has(cacheKey)) return _entityCatalogCache.get(cacheKey);
  if (_entityCatalogInflight.has(cacheKey)) return _entityCatalogInflight.get(cacheKey);
  const params = { kind };
  const tid = teamIdQueryParam(teamId);
  if (tid) params.teamId = tid;
  const promise = axiosInstance
    .get('/insights/entity-properties', { params })
    .then((res) => {
      const rows = flattenEntityCatalog(res.data, kind);
      _entityCatalogCache.set(cacheKey, rows);
      return rows;
    })
    .catch(() => {
      const empty = [];
      _entityCatalogCache.set(cacheKey, empty);
      return empty;
    })
    .finally(() => {
      _entityCatalogInflight.delete(cacheKey);
    });
  _entityCatalogInflight.set(cacheKey, promise);
  return promise;
}

/** Wraps the shared agent Icon for Sidebar / NavIcon usage. */
function AgentNavIcon({ size = 16, className = '', style = {} }) {
  return <span className={`inline-flex ${className}`} style={style}><SpanKindIcon kind="agent" size={size} color="currentColor" /></span>;
}

// Basic event + user predefined properties copied from EventPropertyFilterModule for global use
const getPredefinedEventProperties = () => [
  { name: 'distinct_id', label: 'Distinct ID', type: 'string', source: 'event' },
  { name: 'user_identity', label: 'User Identity', type: 'boolean', source: 'event', special: 'is_identified' },
  { name: 'country', label: 'Country', type: 'string', source: 'event' },
  { name: 'city', label: 'City', type: 'string', source: 'event' },
  { name: 'region', label: 'Region', type: 'string', source: 'event' },
  { name: 'timezone', label: 'Timezone', type: 'string', source: 'event' },
  { name: 'browser_name', label: 'Browser Name', type: 'string', source: 'event' },
  { name: 'browser_version', label: 'Browser Version', type: 'string', source: 'event' },
  { name: 'os', label: 'Operating System', type: 'string', source: 'event' },
  { name: 'os_version', label: 'OS Version', type: 'string', source: 'event' },
  { name: 'device_type', label: 'Device Type', type: 'string', source: 'event' },
  { name: 'language', label: 'Language', type: 'string', source: 'event' },
  { name: 'current_url', label: 'Current URL', type: 'string', source: 'event' },
  { name: 'page_title', label: 'Page Title', type: 'string', source: 'event' },
  { name: 'uri', label: 'URI', type: 'string', source: 'event' },
  { name: 'initial_referrer', label: 'Initial Referrer', type: 'string', source: 'event' },
  { name: 'referrer', label: 'Referrer', type: 'string', source: 'event' },
  { name: 'utm_source', label: 'UTM Source', type: 'string', source: 'event' },
  { name: 'utm_medium', label: 'UTM Medium', type: 'string', source: 'event' },
  { name: 'utm_campaign', label: 'UTM Campaign', type: 'string', source: 'event' },
  { name: 'utm_term', label: 'UTM Term', type: 'string', source: 'event' },
  { name: 'utm_content', label: 'UTM Content', type: 'string', source: 'event' },
  { name: 'utm_id', label: 'UTM ID', type: 'string', source: 'event' },
  { name: 'element_name', label: 'Element Name', type: 'string', source: 'event' },
  { name: 'element_id', label: 'Element ID', type: 'string', source: 'event' },
  { name: 'element_class', label: 'Element Class', type: 'string', source: 'event' },
  { name: 'element_tag_name', label: 'Element Tag Name', type: 'string', source: 'event' },
  { name: 'element_type', label: 'Element Type', type: 'string', source: 'event' },
  { name: 'element_text', label: 'Element Text', type: 'string', source: 'event' },
  { name: 'viewport_height', label: 'Viewport Height', type: 'number', source: 'event' },
  { name: 'viewport_width', label: 'Viewport Width', type: 'number', source: 'event' },
  { name: 'screen_height', label: 'Screen Height', type: 'number', source: 'event' },
  { name: 'screen_width', label: 'Screen Width', type: 'number', source: 'event' },
  { name: 'dpi', label: 'DPI', type: 'number', source: 'event' },
  { name: 'is_web3_user', label: 'Is Web3 User', type: 'boolean', source: 'event' },
  { name: 'wallet_address', label: 'Wallet Address', type: 'boolean', source: 'event', special: 'exists' },
];

const getPredefinedUserProperties = () => getSessionUserProperties();

const ObjectIcon = ({ color = 'currentColor', size = 18 }) => (
  <span style={{ fontSize: size, color }} className="font-mono">
    {'{}'}
  </span>
);

function getDataTypeIcon(type) {
  switch (type) {
    case 'string':
      return Type;
    case 'number':
      return Hash;
    case 'datetime':
      return ({ color = 'currentColor', size = 18 }) => <Icon name="calendar" size={size} color={color} />;
    case 'boolean':
      return ToggleLeft;
    case 'list':
      return List;
    case 'object':
      return ObjectIcon;
    case 'cohort':
      return Users;
    default:
      return Type;
  }
}

function resolveTriggerElement(triggerRef) {
  if (triggerRef == null) return null;
  if (typeof triggerRef === 'object' && triggerRef !== null && 'current' in triggerRef) {
    return triggerRef.current ?? null;
  }
  if (typeof triggerRef === 'object' && typeof triggerRef.getBoundingClientRect === 'function') {
    return triggerRef;
  }
  return null;
}

/**
 * Global Property Filter Module (Insights-style three-pane picker).
 */
const GlobalPropertyFilterModule = ({
  isOpen,
  onClose,
  triggerRef,
  teamId,
  existingFilter = null,
  onGlobalFilterAdd,
  firstTimeOnly = false,
  onFirstTimeChange = null,
  isBreakdownPicker = false,
  // Restricts which category tabs + property rows appear:
  //   'events'  → Events + Users (+ Cohorts + First time)
  //   'runs'    → Agents + Spans + Users (+ Cohorts)
  //   'spans'   → Agents + Spans + Users (+ Cohorts)
  //   undefined → show everything (default — used by Activity/Insights)
  // Mirrors the backend's cross-entity clause rejection in triggerFilterMatching.js.
  entityScope = null,
}) => {
  const scopeAllows = (kind) => {
    if (!entityScope) return true;
    if (kind === 'user' || kind === 'cohort') return true;
    if (entityScope === 'events') return kind === 'event' || kind === 'first_time';
    // runs / spans triggers — agent (run) and span properties are valid.
    if (entityScope === 'runs' || entityScope === 'spans') {
      return kind === 'agent' || kind === 'span';
    }
    return true;
  };
  const [selectedNavItem, setSelectedNavItem] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: 420 });
  const popupRef = useRef(null);

  const [eventProperties] = useState(() => getPredefinedEventProperties());
  // Agents + Spans property catalogs are fetched lazily from
  // /insights/entity-properties and cached at module level so re-opens
  // are instant. Hydrate from cache on first mount when available.
  const [agentsProperties, setAgentsProperties] = useState(() => {
    const tid = teamIdQueryParam(teamId);
    return _entityCatalogCache.get(`${tid || ''}:agent`) || [];
  });
  const [spansProperties, setSpansProperties] = useState(() => {
    const tid = teamIdQueryParam(teamId);
    return _entityCatalogCache.get(`${tid || ''}:span`) || [];
  });
  const [hasLoadedAgents, setHasLoadedAgents] = useState(() => {
    const tid = teamIdQueryParam(teamId);
    return _entityCatalogCache.has(`${tid || ''}:agent`);
  });
  const [hasLoadedSpans, setHasLoadedSpans] = useState(() => {
    const tid = teamIdQueryParam(teamId);
    return _entityCatalogCache.has(`${tid || ''}:span`);
  });
  const [userProperties, setUserProperties] = useState(() => getPredefinedUserProperties());
  const [loadingUserCustom, setLoadingUserCustom] = useState(false);
  const [hasLoadedUserCustom, setHasLoadedUserCustom] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [expandedObjectProperty, setExpandedObjectProperty] = useState(null);
  const [previewProp, setPreviewProp] = useState(null);

  useEffect(() => {
    setHasLoadedUserCustom(false);
    // Reset agent/span hydration on team change so we re-fetch.
    const tid = teamIdQueryParam(teamId);
    setHasLoadedAgents(_entityCatalogCache.has(`${tid || ''}:agent`));
    setHasLoadedSpans(_entityCatalogCache.has(`${tid || ''}:span`));
    setAgentsProperties(_entityCatalogCache.get(`${tid || ''}:agent`) || []);
    setSpansProperties(_entityCatalogCache.get(`${tid || ''}:span`) || []);
  }, [teamId]);

  useEffect(() => {
    if (!isOpen || hasLoadedAgents) return undefined;
    let cancelled = false;
    fetchEntityCatalog(teamId, 'agent').then((rows) => {
      if (cancelled) return;
      setAgentsProperties(rows);
      setHasLoadedAgents(true);
    });
    return () => { cancelled = true; };
  }, [isOpen, teamId, hasLoadedAgents]);

  useEffect(() => {
    if (!isOpen || hasLoadedSpans) return undefined;
    let cancelled = false;
    fetchEntityCatalog(teamId, 'span').then((rows) => {
      if (cancelled) return;
      setSpansProperties(rows);
      setHasLoadedSpans(true);
    });
    return () => { cancelled = true; };
  }, [isOpen, teamId, hasLoadedSpans]);

  useEffect(() => {
    if (!isOpen) setExpandedObjectProperty(null);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    setSelectedNavItem('all');
    setSearchTerm('');
    setPreviewProp(null);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && existingFilter && (existingFilter.category === 'cohort' || existingFilter.propertySource === 'cohort')) {
      setSelectedNavItem('cohorts');
    }
  }, [isOpen, existingFilter]);

  useEffect(() => {
    const fetchUserCustomProps = async () => {
      if (!isOpen || !teamId || hasLoadedUserCustom) return;
      setLoadingUserCustom(true);
      try {
        let numericTeamId = null;
        if (typeof teamId === 'object' && teamId !== null) {
          numericTeamId = parseInt(teamId._id || teamId.id, 10);
        } else if (typeof teamId === 'string') {
          try {
            const teamDataStr = localStorage.getItem('selectedTeamData');
            if (teamDataStr) {
              const teamData = JSON.parse(teamDataStr);
              numericTeamId = parseInt(teamData._id || teamData.id, 10);
            } else {
              numericTeamId = parseInt(teamId, 10);
            }
          } catch {
            numericTeamId = parseInt(teamId, 10);
          }
        } else {
          numericTeamId = parseInt(teamId, 10);
        }
        if (isNaN(numericTeamId) || !numericTeamId) {
          setLoadingUserCustom(false);
          return;
        }

        const res = await axiosInstance.get('/events/user-custom-properties', {
          params: { team_id: numericTeamId, limit: 5000 },
        });

        if (res.data?.success && Array.isArray(res.data.custom_properties)) {
          const custom = res.data.custom_properties.map((prop) => {
            let propertyType = prop.type || 'string';
            let listItemType = null;
            if (propertyType.startsWith('list<') && propertyType.endsWith('>')) {
              listItemType = propertyType.slice(5, -1);
              propertyType = 'list';
            }
            return {
              name: `user.${prop.name}`,
              label: prop.name,
              type: propertyType,
              listItemType,
              source: 'user',
              isCustom: true,
              sampleValues: prop.sampleValues || [],
              nestedProperties: prop.nestedProperties || undefined,
            };
          });

          setUserProperties((prev) => {
            const merged = [...prev, ...custom];
            const byName = new Map();
            merged.forEach((prop) => {
              if (!byName.has(prop.name)) {
                byName.set(prop.name, prop);
              }
            });
            return Array.from(byName.values());
          });
          setHasLoadedUserCustom(true);
        }
      } catch (e) {
        console.error('Error fetching user custom properties for global filter:', e);
      } finally {
        setLoadingUserCustom(false);
      }
    };

    fetchUserCustomProps();
  }, [isOpen, teamId, hasLoadedUserCustom]);

  useEffect(() => {
    const fetchCohorts = async () => {
      if (!isOpen || selectedNavItem !== 'cohorts' || isBreakdownPicker) return;
      const tid = teamIdQueryParam(teamId);
      if (!tid) return;
      setLoadingCohorts(true);
      try {
        const res = await axiosInstance.get('/cohorts', {
          params: { team_id: tid, include_user_count: false },
        });
        if (res.data?.success && Array.isArray(res.data.cohorts)) {
          setCohorts(res.data.cohorts);
        } else {
          setCohorts([]);
        }
      } catch (e) {
        console.error('Error fetching cohorts for global filter:', e);
        setCohorts([]);
      } finally {
        setLoadingCohorts(false);
      }
    };
    fetchCohorts();
  }, [isOpen, selectedNavItem, teamId, isBreakdownPicker]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const popupWidth = 800;
      const desiredHeight = 420;
      const minHeight = 240;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 16;
      const gapBelow = isBreakdownPicker ? 20 : 8;

      let top;
      let left;
      let maxHeight = desiredHeight;

      const triggerEl = resolveTriggerElement(triggerRef);
      if (triggerEl && typeof triggerEl.getBoundingClientRect === 'function') {
        const triggerRect = triggerEl.getBoundingClientRect();
        const spaceBelow = viewportHeight - triggerRect.bottom - gapBelow - margin;
        const spaceAbove = triggerRect.top - gapBelow - margin;

        // Open below by default; flip up only if below has measurably less
        // room *and* above has more. Otherwise stay below and clamp height
        // so the popup never extends past the viewport.
        const fitsBelow = spaceBelow >= Math.min(desiredHeight, minHeight);
        const fitsAbove = spaceAbove >= Math.min(desiredHeight, minHeight);
        const openAbove = !fitsBelow && fitsAbove && spaceAbove > spaceBelow;

        if (openAbove) {
          maxHeight = Math.min(desiredHeight, spaceAbove);
          top = Math.max(margin, triggerRect.top - gapBelow - maxHeight);
        } else {
          maxHeight = Math.min(desiredHeight, Math.max(minHeight, spaceBelow));
          top = triggerRect.bottom + gapBelow;
        }

        left = triggerRect.left;
        if (left + popupWidth > viewportWidth - margin) {
          left = Math.max(margin, triggerRect.right - popupWidth);
        }
        if (left < margin) left = margin;
      } else {
        // No trigger reference — center the popup.
        maxHeight = Math.min(desiredHeight, viewportHeight - 2 * margin);
        left = Math.max(margin, (viewportWidth - popupWidth) / 2);
        top = Math.max(margin, (viewportHeight - maxHeight) / 2);
      }

      // Final clamp: even if our math missed an edge, never let the popup
      // bottom land past the viewport.
      if (top + maxHeight > viewportHeight - margin) {
        maxHeight = Math.max(minHeight, viewportHeight - top - margin);
      }
      if (top < margin) top = margin;

      setPosition({ top, left, maxHeight });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, triggerRef, isBreakdownPicker]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const triggerEl = resolveTriggerElement(triggerRef);
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        triggerEl &&
        !triggerEl.contains(e.target)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const allProps = useMemo(() => {
    const buckets = [];
    if (scopeAllows('event')) buckets.push(...eventProperties);
    if (scopeAllows('agent')) buckets.push(...agentsProperties);
    if (scopeAllows('span'))  buckets.push(...spansProperties);
    if (scopeAllows('user'))  buckets.push(...userProperties);
    return buckets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventProperties, agentsProperties, spansProperties, userProperties, entityScope]);

  const propsByNav = useMemo(() => {
    const cohortRow = { name: 'cohort', label: 'Cohort', type: 'cohort', source: 'cohort' };
    return {
      all: isBreakdownPicker ? [...allProps, cohortRow] : allProps,
      events: scopeAllows('event') ? eventProperties : [],
      agents: scopeAllows('agent') ? agentsProperties : [],
      spans:  scopeAllows('span')  ? spansProperties  : [],
      users:  isBreakdownPicker ? [...userProperties, cohortRow] : userProperties,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProps, eventProperties, agentsProperties, spansProperties, userProperties, isBreakdownPicker, entityScope]);

  const propertiesForNav = useMemo(() => {
    if (selectedNavItem === 'first_time' || selectedNavItem === 'cohorts') return [];
    if (selectedNavItem === 'all') return propsByNav.all;
    if (selectedNavItem === 'events') return propsByNav.events;
    if (selectedNavItem === 'agents') return propsByNav.agents;
    if (selectedNavItem === 'spans') return propsByNav.spans;
    // Legacy 'agent' tab (saved boards, etc.) — fall back to the agents
    // catalog so old session state doesn't render an empty list.
    if (selectedNavItem === 'agent') return propsByNav.agents;
    if (selectedNavItem === 'users') return propsByNav.users;
    return propsByNav.all;
  }, [selectedNavItem, propsByNav]);

  const filteredProps = useMemo(
    () =>
      propertiesForNav.filter(
        (p) =>
          (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [propertiesForNav, searchTerm]
  );

  const filteredCohorts = useMemo(
    () =>
      (Array.isArray(cohorts) ? cohorts : []).filter((c) =>
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [cohorts, searchTerm]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!['all', 'events', 'agents', 'spans', 'agent', 'users'].includes(selectedNavItem)) return;
    if (filteredProps.length === 0) {
      setPreviewProp(null);
      return;
    }
    setPreviewProp((prev) => {
      if (
        prev &&
        !prev.kind &&
        filteredProps.some((p) => p.source === prev.source && p.name === prev.name)
      ) {
        return prev;
      }
      return filteredProps[0];
    });
  }, [isOpen, selectedNavItem, filteredProps]);

  useEffect(() => {
    if (!isOpen || selectedNavItem !== 'cohorts') return;
    if (isBreakdownPicker) {
      setPreviewProp({ kind: 'breakdown_cohort' });
      return;
    }
    if (filteredCohorts.length === 0) {
      setPreviewProp(null);
      return;
    }
    setPreviewProp((prev) => {
      if (prev?.kind === 'cohort' && filteredCohorts.some((c) => c.id === prev.cohort.id)) {
        return prev;
      }
      return { kind: 'cohort', cohort: filteredCohorts[0] };
    });
  }, [isOpen, selectedNavItem, filteredCohorts, isBreakdownPicker]);

  useEffect(() => {
    if (!isOpen || !expandedObjectProperty) return;
    setPreviewProp(expandedObjectProperty);
  }, [isOpen, expandedObjectProperty]);

  const handleAddNestedPropertyFilter = (parentProperty, nestedProp) => {
    if (typeof onGlobalFilterAdd !== 'function') return;
    const nestedType = nestedProp.type || 'string';
    let listItemType = null;
    if (nestedType.startsWith('list<') && nestedType.endsWith('>')) {
      listItemType = nestedType.slice(5, -1);
    }
    const fullPath = `${parentProperty.name}.${nestedProp.name}`;
    const effectiveType = nestedType.startsWith('list') ? 'list' : nestedType;
    onGlobalFilterAdd({
      id: existingFilter?.id || `global-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      property: fullPath,
      propertyLabel: `${parentProperty.label}.${nestedProp.name}`,
      propertyType: effectiveType,
      propertySource: parentProperty.source,
      operator: getDefaultSessionOperator(effectiveType),
      value: null,
      value2: null,
      sampleValues: nestedProp.sampleValues || [],
      listItemType: listItemType || undefined,
      listMode: effectiveType === 'list' ? 'any_in_list' : undefined,
    });
    setExpandedObjectProperty(null);
    onClose();
  };

  const handleAddPropertyFilter = (property) => {
    if (property.type === 'object' && property.nestedProperties?.length > 0) {
      setExpandedObjectProperty(property);
      return;
    }
    if (typeof onGlobalFilterAdd !== 'function') {
      onClose();
      return;
    }

    const baseId = existingFilter?.id || `global-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const didChangeProperty = existingFilter ? existingFilter.property !== property.name : true;
    const operator =
      existingFilter && property.type === existingFilter.propertyType
        ? existingFilter.operator
        : getDefaultSessionOperator(property.type);

    const newFilter = {
      id: baseId,
      property: property.name,
      propertyLabel: property.label,
      propertyType: property.type,
      propertySource: property.source,
      operator,
      value:
        existingFilter && !didChangeProperty && property.type === existingFilter.propertyType
          ? existingFilter.value
          : null,
      value2:
        existingFilter && !didChangeProperty && property.type === existingFilter.propertyType
          ? existingFilter.value2
          : null,
      ...(property.sampleValues?.length && { sampleValues: property.sampleValues }),
      ...(property.listItemType != null && { listItemType: property.listItemType }),
      ...(property.nestedProperties?.length && { nestedProperties: property.nestedProperties }),
      ...(property.type === 'list' && { listMode: existingFilter?.listMode || 'any_in_list' }),
    };

    onGlobalFilterAdd(newFilter);
    onClose();
  };

  const handleCohortClick = (cohort) => {
    if (typeof onGlobalFilterAdd !== 'function') return;
    onGlobalFilterAdd({
      id: `global-cohort-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category: 'cohort',
      propertySource: 'cohort',
      field: 'cohort',
      property: 'cohort',
      value: [cohort.id],
      propertyLabel: cohort.name,
      connector: 'AND',
    });
    onClose();
  };

  const handleBreakdownCohortAdd = () => {
    if (typeof onGlobalFilterAdd !== 'function') return;
    onGlobalFilterAdd({
      property: 'cohort',
      propertyLabel: 'Cohort',
      propertySource: 'cohort',
      category: 'cohort',
    });
    onClose();
  };

  const handleAddFirstTimeFilter = () => {
    if (typeof onGlobalFilterAdd !== 'function') return;
    onGlobalFilterAdd({
      id: `global-first-time-${Date.now()}`,
      category: 'first_time',
      field: 'firstTime',
      property: 'firstTime',
      propertyLabel: 'First time only',
      operator: 'equals',
      value: true,
      connector: 'WHERE',
    });
    onClose();
  };

  const renderDescriptionPane = () => {
    if (expandedObjectProperty) {
      const desc = getPropertyDescriptionPreview(expandedObjectProperty);
      return (
        <>
          <h3 className="mb-2 text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
            {expandedObjectProperty.label}
          </h3>
          <p className="text-xs leading-relaxed text-ink-secondary">
            Choose a nested field. Custom object keys only show the field name until you pick one.
          </p>
          {desc && !desc.titleOnly && (
            <p className="mt-2 text-xs leading-relaxed text-ink-secondary">{desc.body}</p>
          )}
        </>
      );
    }

    if (selectedNavItem === 'first_time') {
      return (
        <>
          <h3 className="mb-2 text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
            First-time sessions
          </h3>
          <p className="text-xs leading-relaxed text-ink-secondary">{getFirstTimeFilterDescription()}</p>
        </>
      );
    }

    if (selectedNavItem === 'cohorts') {
      if (isBreakdownPicker) {
        return (
          <>
            <div className="mb-2 flex items-start gap-2">
              <UsersRound className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: PRIMARY }} />
              <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
                Cohort breakdown
              </h3>
            </div>
            <p className="text-xs leading-relaxed text-ink-secondary">
              Segments the chart by each saved cohort, similar to breaking down by country or device.
            </p>
          </>
        );
      }
      const c = previewProp?.kind === 'cohort' ? previewProp.cohort : null;
      return (
        <>
          <div className="mb-2 flex items-start gap-2">
            <UsersRound className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: PRIMARY }} />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
                {c?.name || 'Cohorts'}
              </h3>
            </div>
          </div>
          <p className="text-xs leading-relaxed text-ink-secondary">
            {c ? getCohortFilterDescription() : 'Select a cohort to filter results to those members.'}
          </p>
        </>
      );
    }

    const prop = previewProp && !previewProp.kind ? previewProp : null;
    if (!prop) {
      if (selectedNavItem === 'agents' || selectedNavItem === 'agent') {
        return (
          <>
            <div className="mb-2 flex items-start gap-2">
              <span className="mt-0.5"><SpanKindIcon kind="agent" size={20} color={PRIMARY} /></span>
              <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
                Agent run properties
              </h3>
            </div>
            <p className="text-xs leading-relaxed text-ink-secondary">
              Filter or break down by properties from agent runs: fixed columns (status, duration, feedback)
              plus custom <span className="font-mono">attributes</span> and <span className="font-mono">metadata</span> keys.
            </p>
          </>
        );
      }
      if (selectedNavItem === 'spans') {
        return (
          <>
            <div className="mb-2 flex items-start gap-2">
              <Wrench className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: PRIMARY }} />
              <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
                Span properties
              </h3>
            </div>
            <p className="text-xs leading-relaxed text-ink-secondary">
              Filter or break down by span properties: fixed columns (kind, model, tool_name, status, tokens, cost)
              plus custom <span className="font-mono">attributes</span> keys.
            </p>
          </>
        );
      }
      return (
        <p className="text-xs" style={{ color: analyticsTheme.textMuted }}>
          Hover a property to see its description.
        </p>
      );
    }

    // New entity property sources (kind=agent or kind=span).
    if (prop.source === 'agent' || prop.source === 'span') {
      const isSpan = prop.source === 'span';
      return (
        <>
          <div className="mb-2 flex items-start gap-2">
            {isSpan
              ? <Wrench className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: PRIMARY }} />
              : <span className="mt-0.5"><SpanKindIcon kind="agent" size={20} color={PRIMARY} /></span>}
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-disabled mb-0.5">{prop.agentGroup || (isSpan ? 'Span' : 'Run')}</div>
              <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
                {prop.label}
              </h3>
            </div>
          </div>
          <p className="text-xs leading-relaxed text-ink-secondary">
            {prop.type === 'number' ? 'Numeric property.' : prop.type === 'boolean' ? 'Boolean property.' : prop.type === 'datetime' ? 'Date/time property.' : 'Text property.'} Applies to {isSpan ? 'span' : 'agent run'} rows.
          </p>
        </>
      );
    }

    // Legacy agent_call / agent_tool / etc. — still render a description for
    // saved-board back-compat.
    if (AGENT_SOURCES.includes(prop.source)) {
      const groupLabels = {
        agent_call: 'Agent Call',
        agent_tool: 'Tool Use',
        agent_response: 'Response',
        agent_error: 'Error',
        agent_feedback: 'Feedback',
      };
      return (
        <>
          <div className="mb-2 flex items-start gap-2">
            <Bot className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: PRIMARY }} />
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-disabled mb-0.5">{groupLabels[prop.source] || 'Agent'}</div>
              <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
                {prop.label}
              </h3>
            </div>
          </div>
          <p className="text-xs leading-relaxed text-ink-secondary">
            {prop.type === 'number' ? 'Numeric property.' : 'Text property.'} Applies to{' '}
            <span className="font-medium">{groupLabels[prop.source] || 'agent'}</span> events.
          </p>
        </>
      );
    }

    if (prop.source === 'cohort' && prop.name === 'cohort') {
      return (
        <>
          <h3 className="mb-2 text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
            Cohort breakdown
          </h3>
          <p className="text-xs leading-relaxed text-ink-secondary">
            Use this dimension to split results by cohort membership across all saved cohorts.
          </p>
        </>
      );
    }

    const desc = getPropertyDescriptionPreview(prop);
    if (!desc) return null;
    if (desc.titleOnly) {
      return (
        <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
          {desc.title}
        </h3>
      );
    }
    return (
      <>
        <h3 className="mb-2 text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
          {desc.title}
        </h3>
        <p className="text-xs leading-relaxed text-ink-secondary">{desc.body}</p>
      </>
    );
  };

  if (!isOpen) return null;

  const baseNav = [
    { id: 'all', label: 'All', NavIcon: LayoutGrid },
    scopeAllows('event') && { id: 'events', label: 'Events', NavIcon: MousePointerClick },
    scopeAllows('agent') && { id: 'agents', label: 'Agents', NavIcon: AgentNavIcon },
    scopeAllows('span')  && { id: 'spans', label: 'Spans', NavIcon: Wrench },
    scopeAllows('user')  && { id: 'users', label: 'Users', NavIcon: Users },
    scopeAllows('cohort') && { id: 'cohorts', label: 'Cohorts', NavIcon: UsersRound },
  ].filter(Boolean);
  const navItems = isBreakdownPicker || !scopeAllows('first_time')
    ? baseNav
    : [...baseNav, { id: 'first_time', label: 'First time', NavIcon: Sparkles }];

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-[color:var(--surface-overlay)]" onClick={onClose} aria-hidden />
      <div
        ref={popupRef}
        className="fixed z-[9999] flex flex-col overflow-hidden rounded-xl border text-left shadow-2xl"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          minWidth: 'min(760px, 96vw)',
          maxWidth: 'min(920px, 96vw)',
          maxHeight: `${position.maxHeight}px`,
          fontFamily: 'Inter, sans-serif',
          backgroundColor: analyticsTheme.bgRaised,
          borderColor: analyticsTheme.border,
        }}
      >
        <div className="flex flex-col sm:flex-row min-h-0 flex-1 overflow-hidden" style={{ minHeight: 280 }}>
          <nav
            className="flex w-full sm:w-[148px] flex-shrink-0 flex-row sm:flex-col overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto border-b sm:border-b-0 sm:border-r"
            style={{ borderColor: analyticsTheme.border, backgroundColor: analyticsTheme.bgNav }}
          >
            <div
              className="hidden sm:block border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]"
              style={{ borderColor: analyticsTheme.border }}
            >
              Categories
            </div>
            <div className="flex flex-1 sm:flex-col flex-row overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto py-1">
              {navItems.map((item) => {
                const active = selectedNavItem === item.id;
                const NavIcon = item.NavIcon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedNavItem(item.id)}
                    className="flex flex-shrink-0 sm:w-full w-auto items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors whitespace-nowrap"
                    style={{
                      backgroundColor: active ? analyticsTheme.navActiveBg : 'transparent',
                      color: active ? PRIMARY : 'var(--text-secondary)',
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    <NavIcon size={14} style={{ color: active ? PRIMARY : 'var(--text-disabled)' }} />
                    {item.label}
                  </button>
                );
              })}
              {loadingUserCustom && (
                <div className="px-3 pt-1 text-[11px]" style={{ color: analyticsTheme.textMuted }}>
                  Loading user properties…
                </div>
              )}
            </div>
          </nav>

          <div
            className="flex min-w-0 flex-1 flex-col border-r"
            style={{ borderColor: analyticsTheme.border }}
          >
            {/* Inline search */}
            <div className="flex items-center gap-2 border-b px-3 py-2 flex-shrink-0" style={{ borderColor: analyticsTheme.border }}>
              <Search size={14} className="text-[color:var(--text-muted)]" />
              <input
                autoFocus
                type="search"
                placeholder={selectedNavItem === 'cohorts' ? 'Search cohorts…' : 'Search properties…'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {expandedObjectProperty ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedObjectProperty(null);
                      setPreviewProp(null);
                    }}
                    className="mb-2 px-3 text-xs font-medium hover:underline"
                    style={{ color: PRIMARY }}
                  >
                    ← Back to properties
                  </button>
                  <div className="px-3 pb-2 text-xs font-medium text-ink-muted">
                    Nested fields in &quot;{expandedObjectProperty.label}&quot;
                  </div>
                  <div className="space-y-0">
                    {expandedObjectProperty.nestedProperties?.map((np) => {
                      const NestedIcon = getDataTypeIcon(np.type?.startsWith('list') ? 'list' : np.type || 'string');
                      return (
                        <button
                          key={np.name}
                          type="button"
                          onClick={() => handleAddNestedPropertyFilter(expandedObjectProperty, np)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-sunken"
                        >
                          <NestedIcon className="h-4 w-4 flex-shrink-0 text-ink-disabled" />
                          <span className="flex-1 text-ink">{np.name}</span>
                          <span className="text-xs text-ink-muted">{np.type || 'string'}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : selectedNavItem === 'first_time' ? (
                <div className="space-y-3 px-3 py-4">
                  <p className="text-sm leading-relaxed" style={{ color: analyticsTheme.textPrimary }}>
                    Restrict this report to users in their first session in the selected time range.
                  </p>
                  {typeof onGlobalFilterAdd === 'function' ? (
                    <button
                      type="button"
                      className="rounded-lg px-3 py-2 text-sm font-medium text-ink-onBrand transition-colors"
                      style={{ backgroundColor: PRIMARY }}
                      onClick={handleAddFirstTimeFilter}
                    >
                      Add first-time filter
                    </button>
                  ) : typeof onFirstTimeChange === 'function' ? (
                    <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-line-strong"
                        checked={firstTimeOnly}
                        onChange={(e) => {
                          onFirstTimeChange(e.target.checked);
                          onClose();
                        }}
                      />
                      <span>First-time sessions only (applies immediately)</span>
                    </label>
                  ) : (
                    <p className="text-sm text-ink-muted">First-time filter is not available here.</p>
                  )}
                </div>
              ) : selectedNavItem === 'cohorts' ? (
                <>
                  {isBreakdownPicker ? (
                    <div className="space-y-3 px-3 py-4">
                      <p className="text-sm leading-relaxed text-ink-secondary">
                        Break down by cohort segments the chart by each saved cohort, like country or device.
                      </p>
                      <button
                        type="button"
                        onClick={handleBreakdownCohortAdd}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-surface-sunken"
                      >
                        <UsersRound className="h-4 w-4 flex-shrink-0 text-ink-muted" />
                        <span>Break down by Cohort</span>
                      </button>
                    </div>
                  ) : loadingCohorts ? (
                    <div className="px-3 py-6 text-center text-sm text-ink-muted">Loading cohorts…</div>
                  ) : filteredCohorts.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-ink-muted">
                      {searchTerm ? 'No cohorts match your search.' : 'No cohorts. Create cohorts on the Cohorts page.'}
                    </div>
                  ) : (
                    filteredCohorts.map((cohort) => {
                      const isPrev = previewProp?.kind === 'cohort' && previewProp.cohort?.id === cohort.id;
                      return (
                        <button
                          key={cohort.id}
                          type="button"
                          onClick={() => handleCohortClick(cohort)}
                          onMouseEnter={() => setPreviewProp({ kind: 'cohort', cohort })}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors"
                          style={{ backgroundColor: isPrev ? analyticsTheme.rowHoverBg : 'transparent' }}
                        >
                          <span className="font-mono text-[color:var(--text-primary)] truncate">{cohort.name}</span>
                          <span className="ml-2 text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">cohort</span>
                        </button>
                      );
                    })
                  )}
                </>
              ) : filteredProps.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-ink-muted">
                  {searchTerm ? 'No properties match.' : 'No properties.'}
                </div>
              ) : (
                (() => {
                  const rows = [];
                  let lastGroup = undefined;
                  filteredProps.forEach((prop, idx) => {
                    // Group header for agent properties
                    if (prop.agentGroup && prop.agentGroup !== lastGroup) {
                      lastGroup = prop.agentGroup;
                      rows.push(
                        <div
                          key={`group-${prop.agentGroup}`}
                          className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)] bg-[color:var(--surface-sunken)]"
                        >
                          {prop.agentGroup}
                        </div>
                      );
                    } else if (!prop.agentGroup) {
                      lastGroup = undefined;
                    }

                    const isSelected =
                      existingFilter &&
                      existingFilter.property === prop.name &&
                      existingFilter.propertySource === prop.source;
                    const isPrev =
                      previewProp &&
                      !previewProp.kind &&
                      previewProp.source === prop.source &&
                      previewProp.name === prop.name;
                    const sourceTag =
                      prop.source === 'user' ? 'user'
                      : prop.source === 'cohort' ? 'cohort'
                      : prop.source === 'span' ? 'span'
                      : prop.source === 'agent' ? 'agent'
                      : AGENT_SOURCES.includes(prop.source) ? 'agent'
                      : (prop.type || 'event');
                    rows.push(
                      <button
                        key={`${prop.source}-${prop.name}-${idx}`}
                        type="button"
                        onClick={() => handleAddPropertyFilter(prop)}
                        onMouseEnter={() => setPreviewProp(prop)}
                        className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors"
                        style={{ backgroundColor: isPrev ? analyticsTheme.rowHoverBg : 'transparent' }}
                      >
                        <span className="font-mono text-[color:var(--text-primary)] truncate">{prop.label}</span>
                        <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">
                          {isSelected && <span style={{ color: PRIMARY }}>current</span>}
                          {sourceTag}
                        </span>
                      </button>
                    );
                  });
                  return rows;
                })()
              )}
            </div>
          </div>

          <div
            className="hidden sm:flex w-[260px] max-w-[38vw] flex-shrink-0 flex-col overflow-y-auto p-3"
            style={{ backgroundColor: analyticsTheme.bgDescription }}
          >
            {renderDescriptionPane()}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default GlobalPropertyFilterModule;
